import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import CustomReportTable from "./custom-report-table";
import ExportDropdown from "./export-dropdown";
import AddButton from "../buttons/button";
import { PlayIcon } from "@heroicons/react/24/outline";
import { customReportExecuteFunctionAction } from "../../redux/actions/custom-report-action";

const FunctionReportRunner = ({ report, functionId }) => {
  const dispatch = useDispatch();
  const filters = report?.filters || [];
  const sortedFilters = [...filters].sort((a, b) => (a.order || 0) - (b.order || 0));

  const [filterValues, setFilterValues] = useState({});
  const [results, setResults] = useState({ columns: [], rows: [], rowCount: 0, truncated: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize filter values with defaults
  useEffect(() => {
    const defaults = {};
    sortedFilters.forEach((f) => {
      if (f.default_value !== null && f.default_value !== undefined && f.default_value !== "" && String(f.default_value).toUpperCase() !== "NULL") {
        defaults[f.param_name] = f.default_value;
      }
    });
    setFilterValues(defaults);
  }, [report]);

  const updateFilterValue = (paramName, value) => {
    setFilterValues((prev) => ({ ...prev, [paramName]: value }));
  };

  const handleRun = () => {
    // Check required filters
    for (const filter of sortedFilters) {
      if (filter.required && !filterValues[filter.param_name]) {
        setError(`"${filter.label}" is required`);
        return;
      }
    }

    setLoading(true);
    setError(null);
    setResults({ columns: [], rows: [], rowCount: 0, truncated: false });

    dispatch(
      customReportExecuteFunctionAction(
        {
          function_id: functionId || report?.function_id,
          filter_values: filterValues,
        },
        (response) => {
          setResults(response.data);
          setLoading(false);
        },
        (errorResponse) => {
          setError(errorResponse?.data?.message || errorResponse?.message || "Execution failed");
          setLoading(false);
        }
      )
    );
  };

  const renderFilterInput = (filter) => {
    const value = filterValues[filter.param_name] || "";

    switch (filter.input_type) {
      case "date":
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => updateFilterValue(filter.param_name, e.target.value)}
            className="w-full px-3 py-2 border border-D0D5DD rounded-md text-sm focus:outline-none focus:border-primary"
          />
        );
      case "datetime":
        return (
          <input
            type="datetime-local"
            value={value}
            onChange={(e) => updateFilterValue(filter.param_name, e.target.value)}
            className="w-full px-3 py-2 border border-D0D5DD rounded-md text-sm focus:outline-none focus:border-primary"
          />
        );
      case "number":
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => updateFilterValue(filter.param_name, e.target.value)}
            placeholder={filter.label}
            className="w-full px-3 py-2 border border-D0D5DD rounded-md text-sm focus:outline-none focus:border-primary"
          />
        );
      case "dropdown":
        return (
          <select
            value={value}
            onChange={(e) => updateFilterValue(filter.param_name, e.target.value)}
            className="w-full px-3 py-2 border border-D0D5DD rounded-md text-sm focus:outline-none focus:border-primary bg-white"
          >
            <option value="">-- Select --</option>
            {(filter.options || []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case "checkbox":
        return (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={value === true || value === "true"}
              onChange={(e) => updateFilterValue(filter.param_name, e.target.checked)}
              className="w-4 h-4 text-primary border-D0D5DD rounded focus:ring-primary"
            />
            <span className="text-sm text-344054">{filter.label}</span>
          </label>
        );
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => updateFilterValue(filter.param_name, e.target.value)}
            placeholder={filter.label}
            className="w-full px-3 py-2 border border-D0D5DD rounded-md text-sm focus:outline-none focus:border-primary"
          />
        );
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Filter Bar */}
      <div className="p-4 border-b border-EAECF0">
        <div className="flex flex-wrap items-end gap-3">
          {sortedFilters.map((filter) => (
            <div key={filter.param_name} className="flex-shrink-0" style={{ minWidth: filter.input_type === "checkbox" ? "auto" : "180px" }}>
              {filter.input_type !== "checkbox" && (
                <label className="block text-xs fw_500 text-344054 mb-1">
                  {filter.label}
                  {filter.required && <span className="text-F51D00 ml-0.5">*</span>}
                </label>
              )}
              {renderFilterInput(filter)}
            </div>
          ))}
          <div className="flex items-center gap-2">
            <AddButton
              text="Run Report"
              icon={<PlayIcon className="w-4 h-4" />}
              onclkFn={handleRun}
              loading={loading}
            />
            <ExportDropdown
              data={results.rows}
              columns={results.columns}
              reportName={report?.name || "Function Report"}
              disabled={results.rows.length === 0}
            />
          </div>
        </div>
        {error && (
          <div className="mt-3 p-3 bg-red-50 border border-F94B4B rounded-md">
            <p className="text-xs text-F51D00">{error}</p>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
          </div>
        ) : (
          <CustomReportTable
            columns={results.columns}
            rows={results.rows}
            rowCount={results.rowCount}
            truncated={results.truncated}
          />
        )}
      </div>
    </div>
  );
};

export default FunctionReportRunner;
